import { makeStyles } from "@material-ui/core/styles";
import { lazy, Suspense, useEffect } from "react";
import { Route, Switch } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import ScrollMemory from "react-router-scroll-memory";
import { reloadAndRedirect_OneTimeReload } from "../../common/functions";
import { useScreenSize } from "../../common/hooks/useScreenSize";
import { useStateLifecycle } from "../../common/hooks/useStateLifecycle";
import { APP_VERSION } from "../../version";
import { ReturnToLocalMenu } from "../LocalDebug/App";
import FooterAnimation from "../shared/Animations/FooterAnimation";
import ShurikenProgress from "../shared/Animations/ShurikenProgress";
import Helmet from "../shared/Helmet";
import { ReMountMain } from "../shared/Remount";
import Layout from "./Layout";
import { invitationHashKey } from "./Layout/Login/MyPage/components/OpenableCards/ItemsCard/items/ItemClass/InvitationCard/functions";
import { logout } from "./Layout/Login/MyPage/MyPageTop/logout";

const Home = lazy(() => import("./zApps/Home"));
const Terms = lazy(() => import("./zApps/Terms"));
const Test = lazy(() => import("./zApps/VocabExplorer"));
const RomajiConverter = lazy(() => import("./zApps/RomajiConverter"));
const KanjiConverter = lazy(() => import("./zApps/KanjiConverter"));
const HiraganaAndKatakana = lazy(() => import("./zApps/HiraganaAndKatakana"));
const HiraganaQuiz = lazy(() => import("./zApps/HiraganaQuiz"));
const KatakanaQuiz = lazy(() => import("./zApps/KatakanaQuiz"));
const VocabList = lazy(() => import("./zApps/Vocab/VocabList"));
const VocabQuiz = lazy(() => import("./zApps/Vocab/VocabQuiz"));
const VocabQuizTop = lazy(() => import("./zApps/Vocab/VocabQuizTop"));
const VocabKanjiQuiz = lazy(() => import("./zApps/Vocab/VocabKanjiQuiz"));
const EnToHiraganaQuiz = lazy(() => import("./zApps/Vocab/EnToHiraganaQuiz"));
const EnToKanjiQuiz = lazy(() => import("./zApps/Vocab/EnToKanjiQuiz"));
const VocabVideo = lazy(() => import("./zApps/Vocab/VocabVideo"));
const VocabEdit = lazy(() => import("./zApps/Vocab/Edit"));
const VocabEditTop = lazy(() => import("./zApps/Vocab/Edit/Top"));
const VocabMerge = lazy(() => import("./zApps/Vocab/Merge"));
const VocabMergeTop = lazy(() => import("./zApps/Vocab/Merge/Top"));
const Stories = lazy(() => import("./zApps/Stories"));
const StoriesTop = lazy(() => import("./zApps/Stories/StoriesTop"));
const StoriesEdit = lazy(() => import("./zApps/Stories/StoriesEdit"));
const StoriesEditTop = lazy(
    () => import("./zApps/Stories/StoriesTop/StoriesEditTop")
);
const StoriesVideo = lazy(() => import("./zApps/Stories/StoriesVideo"));
const GrammarTop = lazy(() => import("./zApps/Grammar/GrammarTop"));
const Grammar = lazy(() => import("./zApps/Grammar/Grammar"));
const KanjiTestTop = lazy(() => import("./zApps/KanjiTest/KanjiTestTop"));
const KanjiTest = lazy(() => import("./zApps/KanjiTest/KanjiTestPage"));
const FlashcardList = lazy(() => import("./zApps/Flashcard"));
const Articles = lazy(() => import("../Articles/Articles"));
const ArticlesTop = lazy(() => import("../Articles/Articles/Top"));
const NinjaTop = lazy(() => import("./zApps/Games/NinjaGameTop"));
const Ninja1 = lazy(() => import("./zApps/Games/NinjaGame"));
const Ninja2 = lazy(() => import("./zApps/Games/NinjaGame2"));
const Ninja3 = lazy(() => import("./zApps/Games/NinjaGame3"));
const GameOver = lazy(() => import("./zApps/Games/GameOver"));
const SiteMapEdit = lazy(() => import("./zApps/SiteMapEdit"));
const Admin = lazy(() => import("./zApps/Admin"));
const LogForAdmin = lazy(() => import("./zApps/Admin/LogForAdmin"));
const ApiCache = lazy(() => import("./zApps/Admin/ApiCache"));
const AbTestResult = lazy(() => import("./zApps/Admin/AbTestResult"));
const OpeLogTable = lazy(() => import("./zApps/Admin/OpeLogTable"));
const GrammarEditTop = lazy(() => import("./zApps/Grammar/QuizEditor/Top"));
const GrammarEdit = lazy(() => import("./zApps/Grammar/QuizEditor/EditPage"));
const KanjiEditTop = lazy(() => import("./zApps/KanjiTest/QuizEditor/Top"));
const KanjiEdit = lazy(() => import("./zApps/KanjiTest/QuizEditor/EditPage"));
const ColorPalette = lazy(() => import("./zApps/ColorPalette"));
const Boscobel = lazy(() => import("./zApps/Boscobel"));
const SBDiner = lazy(() => import("./zApps/SBDiner"));
const ResetPassword = lazy(() => import("./zApps/User/ResetPassword"));
const EnCheck = lazy(() => import("./zApps/EnCheck"));
const NotFound = lazy(() => import("../shared/404"));
const MessageImagePreviewPage = lazy(
    () => import("../shared/Message/MessageImagePreviewPage")
);

export function App() {
    useStateLifecycle();

    return (
        <Layout>
            <ReMountMain>
                <Suspense fallback={<LoadingAnimation />}>
                    <ScrollMemory />
                    <Switch>
                        <Route sensitive exact path="/" component={Home} />
                        <Route sensitive path="/terms" component={Terms} />
                        <Route
                            sensitive
                            path="/developer"
                            component={() => {
                                window.location.href = "/#KosukeZaizen";
                                return null;
                            }}
                        />
                        <Route
                            sensitive
                            path="/u/:userId"
                            component={({
                                match: {
                                    params: { userId },
                                },
                            }: RouteComponentProps<{ userId?: string }>) => {
                                window.location.href = `/#u${userId}`;
                                return null;
                            }}
                        />
                        {/* <Route
                            sensitive
                            path="/kanji-converter"
                            component={KanjiConverter}
                        /> */}
                        <Route
                            sensitive
                            path="/romaji-converter"
                            component={RomajiConverter}
                        />
                        <Route
                            sensitive
                            path="/hiragana-katakana"
                            component={HiraganaAndKatakana}
                        />
                        <Route
                            sensitive
                            path="/hiragana-quiz"
                            component={HiraganaQuiz}
                        />
                        <Route
                            sensitive
                            path="/katakana-quiz"
                            component={KatakanaQuiz}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabulary-list"
                            component={VocabList}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabulary-quiz"
                            component={VocabQuizTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabulary-quiz/:genreName"
                            component={VocabQuiz}
                        />
                        <Route
                            sensitive
                            exact
                            path="/kanji-quiz"
                            component={VocabQuizTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/kanji-quiz/:genreName"
                            component={VocabKanjiQuiz}
                        />
                        <Route
                            sensitive
                            exact
                            path="/EnToHiragana-quiz"
                            component={VocabQuizTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/EnToHiragana-quiz/:genreName"
                            component={EnToHiraganaQuiz}
                        />
                        <Route
                            sensitive
                            exact
                            path="/EnToKanji-quiz"
                            component={VocabQuizTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/EnToKanji-quiz/:genreName"
                            component={EnToKanjiQuiz}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabularyVideo/:genreName"
                            component={VocabVideo}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabularyEdit"
                            component={VocabEditTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabularyEdit/:genreName"
                            component={VocabEdit}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabularyMerge"
                            component={VocabMergeTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/vocabularyMerge/:genreName"
                            component={VocabMerge}
                        />
                        <Route
                            sensitive
                            exact
                            path="/folktales"
                            component={StoriesTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/folktales/:storyName"
                            component={Stories}
                        />
                        <Route
                            sensitive
                            exact
                            path="/folktalesEdit"
                            component={StoriesEditTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/folktalesEdit/:storyName"
                            component={StoriesEdit}
                        />
                        <Route
                            sensitive
                            exact
                            path="/folktalesVideo/:storyName"
                            component={StoriesVideo}
                        />
                        <Route
                            sensitive
                            exact
                            path="/grammar"
                            component={GrammarTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/grammar/:path"
                            component={Grammar}
                        />
                        <Route
                            sensitive
                            exact
                            path="/kanji"
                            component={KanjiTestTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/kanji/:path"
                            component={KanjiTest}
                        />
                        <Route
                            sensitive
                            exact
                            path="/flashcard"
                            component={FlashcardList}
                        />
                        <Route
                            sensitive
                            exact
                            path="/flashcard/:setId"
                            component={FlashcardList}
                        />
                        <Route
                            sensitive
                            exact
                            path="/articles"
                            component={ArticlesTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/articles/:pageName"
                            component={Articles}
                        />
                        <Route sensitive path="/ninja" component={NinjaTop} />
                        <Route sensitive path="/ninja1" component={Ninja1} />
                        <Route sensitive path="/ninja2" component={Ninja2} />
                        <Route sensitive path="/ninja3" component={Ninja3} />
                        <Route
                            sensitive
                            path="/game-over"
                            component={GameOver}
                        />
                        <Route
                            sensitive
                            exact
                            path="/reset_password/:code"
                            component={ResetPassword}
                        />
                        <Route
                            sensitive
                            path="/sitemapEdit"
                            component={SiteMapEdit}
                        />
                        <Route sensitive path="/admin" component={Admin} />
                        <Route
                            sensitive
                            path="/logForAdmin/:targetUserId"
                            component={LogForAdmin}
                        />
                        <Route
                            sensitive
                            path="/apiCache"
                            component={ApiCache}
                        />
                        <Route
                            sensitive
                            path="/opeLogTable"
                            component={OpeLogTable}
                        />
                        <Route
                            sensitive
                            exact
                            path="/grammarEdit"
                            component={GrammarEditTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/grammarEdit/:path"
                            component={GrammarEdit}
                        />
                        <Route
                            sensitive
                            exact
                            path="/kanjiEdit"
                            component={KanjiEditTop}
                        />
                        <Route
                            sensitive
                            exact
                            path="/kanjiEdit/:path"
                            component={KanjiEdit}
                        />
                        <Route
                            sensitive
                            path="/abTestResult"
                            component={AbTestResult}
                        />
                        <Route
                            sensitive
                            path="/color-code"
                            component={ColorPalette}
                        />
                        <Route
                            sensitive
                            path="/boscobel"
                            component={Boscobel}
                        />
                        <Route sensitive path="/sb-diner" component={SBDiner} />
                        <Route
                            sensitive
                            path="/en-check/:pageName?"
                            component={EnCheck}
                        />
                        <Route
                            sensitive
                            exact
                            path="/message-image-preview/:myUserId/:targetUserId/:messageNo"
                            component={MessageImagePreviewPage}
                        />
                        <Route sensitive path="/test" component={Test} />
                        <Route
                            sensitive
                            path="/invitation/:encodedEmail"
                            component={RedirectForInvitation}
                        />
                        <Route
                            sensitive
                            path="/not-found"
                            component={NotFound}
                        />
                        <Route
                            sensitive
                            exact
                            path="/local"
                            component={ReturnToLocalMenu}
                        />
                        <Route component={NotFoundRedirect} />
                    </Switch>
                </Suspense>
            </ReMountMain>

            <FooterAnimation />
        </Layout>
    );
}

function NotFoundRedirect() {
    const url = `api/SystemBase/GetVersion/V${new Date().getMilliseconds()}`;
    fetch(url).then(res => {
        res.json().then(v => {
            if (Number(v) !== APP_VERSION) {
                //@ts-ignore
                window.location.reload(true);
            } else {
                reloadAndRedirect_OneTimeReload("pageNotFoundRedirect");
            }
        });
    });

    return (
        <>
            <Helmet noindex />
            <LoadingAnimation />
        </>
    );
}

function RedirectForInvitation({
    match: {
        params: { encodedEmail },
    },
}: RouteComponentProps<{ encodedEmail?: string }>) {
    useEffect(() => {
        logout(true).then(() => {
            window.location.href = `/${invitationHashKey}${encodedEmail}`;
        });
    }, [encodedEmail]);
    return (
        <>
            <Helmet noindex />
            <LoadingAnimation />
        </>
    );
}

export function LoadingAnimation() {
    const { screenWidth, screenHeight } = useScreenSize();
    const c = useLoadingAnimationStyles();
    return (
        <div className={c.container}>
            <ShurikenProgress size={Math.min(screenWidth, screenHeight) / 5} />
        </div>
    );
}
const useLoadingAnimationStyles = makeStyles({
    container: {
        width: "100%",
        height: "100%",
        position: "fixed",
        top: 0,
        left: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});
